/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { OmitFunctions } from 'src/core/stores/types';
import { LuisId } from 'src/luis/types/Resource';

type ConversationFeatureStoreData = OmitFunctions<Omit<ConversationFeatureStore, 'id'>>;

export class ConversationFeatureStore {
    public id: LuisId;

    public typeId: 'feature' | 'model';

    @observable public name: string;

    @observable public isRequired: boolean;

    constructor(id: LuisId, feature: Partial<ConversationFeatureStoreData>) {
        this.id = id;
        this.typeId = feature.typeId;
        this.name = feature.name;
        this.isRequired = feature.isRequired || false;
    }

    /**
     * Sets a feature name.
     * @param name New name for the feature.
     */
    @action
    public setName(name: string) {
        this.name = name;
    }

    /**
     * Sets a feature isRequired.
     * @param required New value.
     */
    @action
    public setRequired(required: boolean) {
        this.isRequired = required;
    }
}

/**
 * An feature store factory object.
 *
 * @param id The id of the feature to create.
 * @param feature The feature data.
 */
export const createConversationFeatureStore = (id: LuisId, feature: Partial<ConversationFeatureStoreData>) =>
    new ConversationFeatureStore(id, feature);
